import React, { useEffect, useState } from "react";
import { IoIosMicrophone } from "react-icons/io";
import {
  useAudioRecorder,
  onNotAllowedOrFound,
} from "react-audio-voice-recorder";
import { Button, SecondaryButton, toast } from "@jammable/ui-core";
import styles from "../../styles/Conversion.module.css";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { v4 as uuidv4 } from "uuid";
import ModelConvertSettings from "./ModelConvertSettings";
import {
  FaCheckCircle,
  FaPauseCircle,
  FaPlayCircle,
  FaStopCircle,
} from "react-icons/fa";
import AudioPlayerMic from "../AudioPlayerMic";
import fixWebmDuration from "webm-duration-fix";
import { Spinner } from "@jammable/ui-components/Loader";

const MicrophoneConversionFlow = ({
  model,
  ip,
  setIsOpen,
  setUpgradeModalOpen,
}) => {
  const [finalUrl, setFinalUrl] = useState(null);
  const [voices, setVoices] = useState([{ ...model, pitchScaling: 0 }]);

  const handleNotAllowedOrFound = (domException) => {
    console.error("Error accessing microphone:", domException);
  };
  const {
    startRecording,
    stopRecording,
    isRecording,
    recordingBlob,
    togglePauseResume,
    isPaused,
    recordingTime,
  } = useAudioRecorder({
    onNotAllowedOrFound: handleNotAllowedOrFound,
  });
  const [blob, setBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualStop, setManualStop] = useState(false);

  useEffect(() => {
    if (!recordingBlob || manualStop) return;

    const url = URL.createObjectURL(recordingBlob);
    setBlob(recordingBlob);
    setAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [recordingBlob, manualStop]);

  const uploadToS3 = async () => {
    if (!blob) return;

    try {
      setLoading(true);
      // Fix the webm duration before uploading
      let fixedBlob;
      await fixWebmDuration(blob)
        .then((res) => {
          fixedBlob = res;
        })
        .catch((err) => {
          fixedBlob = blob;
        });

      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const response = await axios.get("/api/s3/modelS3PresignedUrl", {
        params: { model: model?.urlParam, ip: ip },
        headers: { Authorization: token },
      });
      const { url, key, absoluteUrl } = response.data;

      const result = await axios.put(url, fixedBlob, {
        headers: {
          "Content-Type": "video/webm",
        },
      });
      setLoading(false);

      if (result.status === 200) {
        const s3Url = absoluteUrl;
        setFinalUrl(s3Url);
      } else {
        console.error("Failed to upload audio:", result);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error uploading audio to S3:", error);

      console.log(error);
      setLoading(false);
      if (error?.response?.data?.message === "You are out of credits") {
        setIsOpen(false);
        setUpgradeModalOpen({ isOpen: true, dailyLimit: true });
      } else {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  const handleStopRecording = () => {
    stopRecording();
    setManualStop(true);
    setBlob(null);
    setAudioUrl(null);
  };
  const formatSeconds = (s) => {
    const minutes = Math.floor(s / 60);
    const seconds = s % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const setPitchScalingForVoice = (voiceId, pitch) => {
    const updatedVoices = voices.map((voice) => {
      if (voice._id === voiceId) {
        return { ...voice, pitchScaling: pitch };
      }
      return voice;
    });
    setVoices(updatedVoices);
  };

  return (
    <div
      className={`tw-w-full ${finalUrl ? "md:tw-w-[30rem]" : "md:tw-w-[30rem]"} tw-h-max`}
    >
      {finalUrl ? (
        <ModelConvertSettings
          voices={voices}
          finalUrl={finalUrl}
          mode="microphone"
          ip={ip}
          progress={100}
          setIsOpen={setIsOpen}
          setUpgradeModalOpen={setUpgradeModalOpen}
          setPitchScalingForVoice={setPitchScalingForVoice}
        />
      ) : (
        <>
          <div className="tw-text-xl tw-font-bold tw-text-center">
            Microphone Cover
          </div>
          <div className="tw-flex tw-flex-col tw-w-full tw-items-center tw-p-3 tw-pb-1 tw-pt-4">
            <div
              className={`tw-w-72 tw-h-72 tw-border-2 tw-border-solid tw-flex tw-items-center tw-justify-center tw-rounded-full ${isRecording && !isPaused ? styles["animate-ring2"] : "tw-border-purple-400"}`}
            >
              <div
                className={`tw-w-56 tw-h-56 tw-border-2 tw-border-solid tw-flex tw-items-center tw-justify-center tw-rounded-full ${isRecording && !isPaused ? styles["animate-ring3"] : "tw-border-purple-700"}`}
              >
                <div
                  onClick={() => {
                    if (isRecording) {
                      stopRecording();
                    } else {
                      startRecording();
                    }
                  }}
                  className="tw-w-40 tw-h-40 tw-bg-purple-700 tw-flex tw-justify-center tw-rounded-full tw-relative"
                >
                  <IoIosMicrophone
                    className="tw-text-white tw-absolute tw--bottom-4"
                    size={140}
                  />
                </div>
              </div>
            </div>
            {audioUrl && (
              <AudioPlayerMic className="tw-mt-2" controls url={audioUrl} />
            )}

            {!blob && (
              <div className="tw-bg-gray-100 tw-p-5 tw-text-purple-700 tw-font-bold tw-mt-6 tw-rounded-xl tw-py-2">
                {formatSeconds(recordingTime)}
              </div>
            )}
            <div
              className={`tw-flex tw-gap-2 tw-items-center tw-mt-4 tw-w-full  ${styles.transition}`}
            >
              {!blob && !isRecording && (
                <Button
                  onClick={() => {
                    if (blob) {
                      setBlob(null);
                      setAudioUrl(null);
                      setManualStop(false);
                    } else {
                      startRecording();
                    }
                  }}
                  className={` tw-rounded-full tw-w-full`}
                >
                  {"Start Recording"}
                </Button>
              )}

              {!blob && isRecording && (
                <Button
                  onClick={handleStopRecording}
                  style={{ paddingInline: 10 }}
                  className={`tw-rounded-full tw-w-full tw-bg-red-500 hover:tw-bg-red-600 tw-border-none ${styles["zoom-in"]}`}
                >
                  <FaStopCircle size={30} />
                </Button>
              )}
              {/* {!blob && isRecording && (
                <Button
                  onClick={() => {
                    togglePauseResume();
                  }}
                  style={{ paddingInline: 10 }}
                  className={`tw-rounded-full  tw-w-full tw-pl-0 ${styles["zoom-in"]}`}
                >
                  {isPaused ? (
                    <FaPlayCircle size={30} />
                  ) : (
                    <FaPauseCircle size={30} />
                  )}
                </Button>
              )} */}
              {!blob && isRecording && (
                <Button
                  onClick={() => {
                    setManualStop(false);
                    stopRecording();
                  }}
                  style={{ paddingInline: 10 }}
                  className={`tw-rounded-full tw-w-full tw-bg-green-400 hover:tw-bg-green-500 tw-border-none ${styles["zoom-in"]}`}
                >
                  <FaCheckCircle size={30} />
                </Button>
              )}
              {blob && (
                <SecondaryButton
                  onClick={() => {
                    setBlob(null);
                    setAudioUrl(null);
                  }}
                  className={`tw-rounded-full tw-w-full ${styles["zoom-in"]}`}
                  disabled={!blob}
                >
                  Redo
                </SecondaryButton>
              )}
              {blob && (
                <Button
                  onClick={uploadToS3}
                  className={`tw-rounded-full tw-w-full ${styles["zoom-in"]}`}
                  disabled={!blob || loading}
                >
                  {loading ? (
                    <Spinner className="tw-border-white tw-border-3" size={4} />
                  ) : (
                    "Continue"
                  )}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MicrophoneConversionFlow;
